import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Spinner,
  Button,
  Input,
  Card,
  Badge,
  Checkbox,
  Dropdown,
  Option,
  tokens,
} from '@fluentui/react-components';
import {
  DocumentRegular,
  CheckmarkCircleRegular,
  DismissCircleRegular,
  ArrowUploadRegular,
  PersonRegular,
  BuildingRegular,
  PhoneRegular,
  MailRegular,
  KeyRegular,
  LockClosedRegular,
} from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import portalI18n from '../i18n/portalI18n';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
const SESSION_STORAGE_KEY = 'ptc_portal_session';

interface StoredSession {
  token: string;
  identifier: string;
  expiresAt: string;
}

interface TaxReturnInfo {
  id: string;
  clientName: string;
  taxYear: number;
  returnType: string;
  filingType?: string;
  status: string;
  preparerName?: string;
  scheduledDate?: string;
  allDocumentsReceived: boolean;
  missingDocuments: string[];
  spouseName?: string;
  statusDisplayName?: string;
}

interface UploadReceipt {
  emailed: boolean;
  files: Array<{ fileName: string; attached: boolean }>;
  attachedCount: number;
  generalCount: number;
  taxYears: number[];
}

interface UploadedFile {
  name: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
}

type PortalStep = 'loading' | 'identify' | 'verify' | 'mfa' | 'returns' | 'upload' | 'setPassword';

interface DeviceFingerprint {
  userAgent: string;
  screenResolution: string;
  timezone: string;
  language: string;
}

/**
 * How long the resend button stays on its countdown, and how long we wait before offering the
 * other channel. Deliberately two constants that happen to share a value: they answer different
 * questions and must be able to move independently.
 *
 * Both exist because of the 2026-08-26 incident — an OTP text that Twilio accepted, billed and
 * delivered roughly seven minutes late, in front of a screen with no resend, no other channel and
 * nothing that ever admitted the wait. See UploadPortalPage.otpHonesty.test.tsx.
 */
const RESEND_COOLDOWN_SECONDS = 30;
const EMAIL_FALLBACK_AFTER_SECONDS = 30;

export default function UploadPortalPage() {
  const { t } = useTranslation('translation', { i18n: portalI18n });
  const [step, setStep] = useState<PortalStep>('loading');
  const [identifier, setIdentifier] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [hasPassword, setHasPassword] = useState(false);
  const [usePasswordLogin, setUsePasswordLogin] = useState(false);
  const [requiresMfa, setRequiresMfa] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [clientName, setClientName] = useState('');
  const [returns, setReturns] = useState<TaxReturnInfo[]>([]);
  const [selectedReturn, setSelectedReturn] = useState<TaxReturnInfo | null>(null);
  const [isGeneralUpload, setIsGeneralUpload] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [receipt, setReceipt] = useState<UploadReceipt | null>(null);
  const [selectedDocType, setSelectedDocType] = useState<string>('other');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verificationType, setVerificationType] = useState<'email' | 'phone'>('email');
  // Seconds since the code was last actually accepted by the sender. Drives the resend countdown
  // and the other-channel offer. Reset on every send, not on every render.
  const [secondsSinceSend, setSecondsSinceSend] = useState(0);
  const [sending, setSending] = useState(false);
  const [showEmailHint, setShowEmailHint] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Deep-link: if notification sent client here for a specific return, auto-select it
  const requestedReturnId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('returnId');
  }, []);

  // Auto-select the requested return once returns are loaded
  useEffect(() => {
    if (step !== 'returns' || !requestedReturnId || returns.length === 0) return;
    const match = returns.find(r => r.id === requestedReturnId);
    if (match) {
      setSelectedReturn(match);
      setIsGeneralUpload(false);
      setUploadedFiles([]);
      setStep('upload');
    }
  }, [step, requestedReturnId, returns]);

  // A Request Documents link names a specific return via ?returnId=. If that return is not among
  // the ones this verified identity can see, we must SAY SO.
  //
  // This is the client-facing half of the 2026-08-17 SanMarth incident. The owner followed a link
  // for his company's return; portal visibility is keyed on the return's clientEmail/clientPhone
  // (see GetTaxReturnsByClientContactAsync), and that return carried the company's email and no
  // phone, so it was never in his list. He was told nothing, used general upload instead, and the
  // documents sat unattached for a day. Silently ignoring a link the client was explicitly sent is
  // the defect — not the mismatch itself, which is a data question (spec §5.A).
  //
  // Evaluated only while `step === 'returns'`, which is reached after the list has loaded, so an
  // empty `returns` here means "none visible", not "not yet fetched".
  const deepLinkTargetMissing = useMemo(
    () => Boolean(requestedReturnId) && !returns.some(r => r.id === requestedReturnId),
    [requestedReturnId, returns]
  );

  // Get device fingerprint
  const getDeviceFingerprint = (): DeviceFingerprint => ({
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
  });

  // Check for existing session on mount
  useEffect(() => {
    const checkExistingSession = async () => {
      try {
        const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (stored) {
          const session: StoredSession = JSON.parse(stored);

          // Check if session is expired
          if (new Date(session.expiresAt) > new Date()) {
            // Try to use the session
            const response = await fetch(`${API_BASE}/portal/returns`, {
              headers: { 'X-Portal-Session': session.token },
            });

            if (response.ok) {
              const data = await response.json();
              setSessionToken(session.token);
              setIdentifier(session.identifier);
              setClientName(data.data?.clientName || '');
              setReturns(data.data?.returns || []);
              setStep('returns');
              return;
            }
          }

          // Session invalid or expired, clear it
          sessionStorage.removeItem(SESSION_STORAGE_KEY);
        }
      } catch (e) {
        // Ignore errors, just show login
        sessionStorage.removeItem(SESSION_STORAGE_KEY);
      }

      setStep('identify');
    };

    checkExistingSession();
  }, []);

  // Check if user has password and device trust when they enter identifier
  const handleCheckIdentifier = async () => {
    if (!identifier) return;

    setLoading(true);
    setError(null);

    try {
      // Check device and password status
      const response = await fetch(`${API_BASE}/portal/device/check`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          device: getDeviceFingerprint(),
        }),
      });
      const data = await response.json();

      if (data.data) {
        setHasPassword(data.data.hasPassword || false);
        setRequiresMfa(data.data.requiresMfa || false);

        // If device is trusted or no password, suggest verification code
        // If device not trusted and has password, MFA will be required
        if (data.data.hasPassword && !data.data.deviceTrusted) {
          setUsePasswordLogin(false); // Will need MFA, so start with code
        } else if (data.data.hasPassword) {
          setUsePasswordLogin(true); // Device trusted, can use password only
        }
      }
    } catch {
      // Ignore errors, just proceed without special handling
    } finally {
      setLoading(false);
    }
  };

  // While we are sitting on the verify screen, count the seconds since the code was accepted.
  // This is the only clock on this page; the resend countdown and the other-channel offer are both
  // derived from it, so they can never disagree about how long she has been waiting.
  useEffect(() => {
    if (step !== 'verify') return;
    const timer = setInterval(() => setSecondsSinceSend(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [step]);

  /**
   * Asks the sender for a code. Shared by the first send and by Resend, so the two can never drift
   * — a "Resend" that re-renders a label without issuing a request is the sent-forever family.
   */
  const requestCode = async () => {
    setSending(true);
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/portal/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.sendCodeFailed'));
      }

      // Only now has anything actually been accepted for delivery. Everything the client is told
      // in the past tense hangs off this line.
      setVerificationType(data.data?.verificationType || 'email');
      setUsePasswordLogin(false);
      setShowEmailHint(false);
      setSecondsSinceSend(0);
      setStep('verify');
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
      return false;
    } finally {
      setSending(false);
      setLoading(false);
    }
  };

  // Step 1: Send verification code
  const handleSendCode = () => requestCode();

  // Ask for another one, without throwing away the screen she is on. Before this existed the only
  // way back to the sender was "Use different email/phone" — a round trip through the identify
  // step, which is exactly the 47-second detour the incident log shows.
  const handleResendCode = () => requestCode();

  const resendIn = Math.max(0, RESEND_COOLDOWN_SECONDS - secondsSinceSend);

  // Phone channel only, and only while she has typed nothing — an offer that interrupts someone
  // halfway through a code they DID receive is a different kind of unhelpful.
  const offerEmailInstead =
    verificationType === 'phone' &&
    verificationCode.length === 0 &&
    secondsSinceSend >= EMAIL_FALLBACK_AFTER_SECONDS;

  /**
   * The offer she had to invent for herself. Takes her to the identifier field, cleared and
   * explained, rather than promising a send to an address this page has never been told.
   */
  const handleUseEmailInstead = () => {
    setIdentifier('');
    setVerificationCode('');
    setError(null);
    setHasPassword(false);
    setUsePasswordLogin(false);
    setShowEmailHint(true);
    setSecondsSinceSend(0);
    setStep('identify');
  };

  // Step 2: Verify code
  const handleVerifyCode = async () => {
    setLoading(true);
    setError(null);

    try {
      // If MFA required and has password, go to MFA step after code verification
      if (requiresMfa && hasPassword) {
        // Just validate the code format, actual verification happens in MFA
        if (verificationCode.length !== 6) {
          throw new Error(t('portal.errors.incompleteCode'));
        }
        setStep('mfa');
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_BASE}/portal/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, code: verificationCode }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.invalidCode'));
      }

      const token = data.data?.sessionToken;
      const expiresAt = data.data?.expiresAt;

      if (!token) {
        throw new Error(t('portal.errors.noSessionToken'));
      }

      setSessionToken(token);

      // Save session to sessionStorage for future visits
      const session: StoredSession = {
        token,
        identifier,
        expiresAt,
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

      // Fetch returns
      await fetchReturns(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Login with password
  const handlePasswordLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/portal/password/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ identifier, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.loginFailed'));
      }

      const token = data.data?.sessionToken;
      const expiresAt = data.data?.expiresAt;

      if (!token) {
        throw new Error(t('portal.errors.noSessionToken'));
      }

      setSessionToken(token);

      // Save session to sessionStorage
      const session: StoredSession = {
        token,
        identifier,
        expiresAt,
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

      // Fetch returns
      await fetchReturns(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Set password
  const handleSetPassword = async () => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);

    if (password !== confirmPassword) {
      setError(t('portal.setPassword.errorMismatch'));
      setLoading(false);
      return;
    }

    if (password.length < 8) {
      setError(t('portal.setPassword.errorTooShort'));
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/portal/password/set`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Portal-Session': sessionToken || '',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.setPasswordFailed'));
      }

      setSuccessMessage(t('portal.setPassword.successMessage'));
      setHasPassword(true);
      setPassword('');
      setConfirmPassword('');

      // Go back to returns after short delay
      setTimeout(() => {
        setStep('returns');
        setSuccessMessage(null);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Complete MFA (both code and password)
  const handleCompleteMfa = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/portal/mfa/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          identifier,
          code: verificationCode,
          password,
          device: getDeviceFingerprint(),
          trustDevice,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.mfaFailed'));
      }

      const token = data.data?.sessionToken;
      const expiresAt = data.data?.expiresAt;

      if (!token) {
        throw new Error(t('portal.errors.noSessionToken'));
      }

      setSessionToken(token);
      setPassword(''); // Clear password

      // Save session to sessionStorage
      const session: StoredSession = {
        token,
        identifier,
        expiresAt,
      };
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));

      // Fetch returns
      await fetchReturns(token);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Fetch client's tax returns
  const fetchReturns = async (token: string) => {
    try {
      const response = await fetch(`${API_BASE}/portal/returns`, {
        headers: { 'X-Portal-Session': token },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || t('portal.errors.loadReturnsFailed'));
      }

      setClientName(data.data?.clientName || '');
      setReturns(data.data?.returns || []);
      setStep('returns');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('portal.errors.generic'));
    }
  };

  // Select a return to upload documents
  const handleSelectReturn = (taxReturn: TaxReturnInfo) => {
    setSelectedReturn(taxReturn);
    setIsGeneralUpload(false);
    setUploadedFiles([]);
    setStep('upload');
  };

  // General upload (not tied to a specific return)
  const handleGeneralUpload = () => {
    setSelectedReturn(null);
    setIsGeneralUpload(true);
    setUploadedFiles([]);
    setStep('upload');
  };

  // Handle file upload
  const handleFileUpload = useCallback(async (files: FileList | null) => {
    if (!files || !sessionToken) return;
    // For specific return uploads, we need selectedReturn; for general uploads we don't
    if (!isGeneralUpload && !selectedReturn) return;

    const fileArray = Array.from(files);
    // Ids of everything that lands in THIS batch. The receipt is per batch, not per file:
    // one email per document would have sent Maria Stopa thirteen of them in three minutes.
    const batchDocumentIds: string[] = [];

    // Add files to list with pending status
    setUploadedFiles(prev => [
      ...prev,
      ...fileArray.map(f => ({ name: f.name, status: 'pending' as const })),
    ]);

    // Upload each file
    for (const file of fileArray) {
      setUploadedFiles(prev =>
        prev.map(f => f.name === file.name ? { ...f, status: 'uploading' as const } : f)
      );

      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('documentType', selectedDocType);
        // Carry deep-link context through to the backend so general uploads
        // triggered by a notification can still be attributed to the source return.
        if (requestedReturnId) {
          formData.append('sourceReturnId', requestedReturnId);
        }

        // Use different endpoint for general uploads vs return-specific uploads
        const uploadUrl = isGeneralUpload
          ? `${API_BASE}/portal/upload`
          : `${API_BASE}/portal/returns/${selectedReturn!.id}/upload`;

        const response = await fetch(uploadUrl, {
          method: 'POST',
          headers: { 'X-Portal-Session': sessionToken },
          body: formData,
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || t('portal.errors.uploadFailed'));
        }

        if (data?.data?.documentId) batchDocumentIds.push(data.data.documentId);

        setUploadedFiles(prev =>
          prev.map(f => f.name === file.name ? { ...f, status: 'success' as const } : f)
        );
      } catch (err) {
        setUploadedFiles(prev =>
          prev.map(f =>
            f.name === file.name
              ? { ...f, status: 'error' as const, error: err instanceof Error ? err.message : t('portal.errors.uploadFailed') }
              : f
          )
        );
      }
    }

    // The receipt confirms what actually landed. A failure here must never look like a failed
    // upload -- the documents are already stored -- so it only ever clears the receipt.
    if (batchDocumentIds.length > 0) {
      try {
        const receiptResponse = await fetch(`${API_BASE}/portal/uploads/receipt`, {
          method: 'POST',
          headers: {
            'X-Portal-Session': sessionToken,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ documentIds: batchDocumentIds }),
        });
        const receiptData = await receiptResponse.json();
        if (receiptResponse.ok && receiptData?.data) setReceipt(receiptData.data as UploadReceipt);
      } catch {
        setReceipt(null);
      }
    }
  }, [selectedReturn, sessionToken, isGeneralUpload, selectedDocType, requestedReturnId, t]);

  // Drag and drop handler
  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileUpload(e.dataTransfer.files);
  }, [handleFileUpload]);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #8a9a5b 0%, #6b7a3d 100%)',
      padding: '20px',
      // Fleet ruling (Christian, 2026-10-28): the system font everywhere, incl. the client portal --
      // Fluent v9's own stack, verbatim; no web font loads here.
      fontFamily: "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serif",
    }}>
      <div style={{
        maxWidth: '500px',
        margin: '0 auto',
      }}>
        {/* Header */}
        <div style={{
          textAlign: 'center',
          color: 'white',
          marginBottom: '24px',
        }}>
          {/* PTC Logo - stylized wave/chevron marks */}
          <div style={{ marginBottom: '8px', fontSize: '36px', letterSpacing: '2px', fontWeight: 700 }}>
            <span style={{ color: '#ffffff' }}>&laquo;</span>
            <span style={{ color: '#c8d8a0' }}>&raquo;</span>
          </div>
          <h1 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeHero700, fontWeight: 700, letterSpacing: '0.5px', color: '#ffffff' }}>{t('portal.brand.name')}</h1>
          <p style={{ margin: 0, opacity: 0.9 }}>{t('portal.header.subtitle')}</p>
        </div>

        {/* Main Card */}
        <Card style={{
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
          backgroundColor: '#ffffff',
        }}>
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fde7e9',
              color: '#a4262c',
              borderRadius: '4px',
              marginBottom: '16px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <DismissCircleRegular />
              {error}
            </div>
          )}

          {/* Loading state */}
          {step === 'loading' && (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Spinner size="large" label={t('portal.loading')} />
            </div>
          )}

          {/* Step 1: Enter email or phone */}
          {step === 'identify' && (
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeBase500 }}>{t('portal.identify.title')}</h2>
              <p style={{ color: '#605e5c', marginBottom: '24px' }}>
                {t('portal.identify.subtitle')}
              </p>

              {showEmailHint && (
                <p
                  data-testid="email-fallback-hint"
                  style={{ color: '#605e5c', marginBottom: '16px' }}
                >
                  {t('portal.identify.emailHint')}
                </p>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.identify.fieldLabel')}
                </label>
                <Input
                  value={identifier}
                  onChange={(_, data) => {
                    setIdentifier(data.value);
                    setHasPassword(false);
                    setUsePasswordLogin(false);
                  }}
                  onBlur={handleCheckIdentifier}
                  placeholder={t('portal.identify.placeholder')}
                  style={{ width: '100%' }}
                  contentBefore={identifier.includes('@') ? <MailRegular /> : <PhoneRegular />}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && identifier) {
                      if (usePasswordLogin && password) {
                        handlePasswordLogin();
                      } else if (!usePasswordLogin) {
                        handleSendCode();
                      }
                    }
                  }}
                />
              </div>

              {/* Password login option */}
              {hasPassword && usePasswordLogin && (
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                    {t('portal.common.passwordLabel')}
                  </label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(_, data) => setPassword(data.value)}
                    placeholder={t('portal.common.passwordPlaceholder')}
                    style={{ width: '100%' }}
                    contentBefore={<LockClosedRegular />}
                    onKeyDown={(e) => e.key === 'Enter' && password && handlePasswordLogin()}
                  />
                </div>
              )}

              {/* Login buttons */}
              {hasPassword && usePasswordLogin ? (
                <>
                  <Button
                    appearance="primary"
                    style={{ width: '100%', marginBottom: '12px', backgroundColor: '#1a1a1a', color: '#ffffff', borderColor: '#1a1a1a' }}
                    onClick={handlePasswordLogin}
                    disabled={!identifier || !password || loading}
                  >
                    {loading ? <Spinner size="tiny" /> : t('portal.identify.signInButton')}
                  </Button>
                  <Button
                    appearance="subtle"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setUsePasswordLogin(false);
                      setPassword('');
                    }}
                  >
                    {t('portal.identify.useCodeInstead')}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    appearance="primary"
                    style={{ width: '100%', marginBottom: hasPassword ? '12px' : '0', backgroundColor: '#1a1a1a', color: '#ffffff', borderColor: '#1a1a1a' }}
                    onClick={handleSendCode}
                    disabled={!identifier || loading}
                  >
                    {loading ? <Spinner size="tiny" /> : t('portal.identify.sendCodeButton')}
                  </Button>
                  {sending && (
                    <p
                      data-testid="sending-notice"
                      style={{ color: '#605e5c', marginTop: '12px', marginBottom: 0 }}
                    >
                      {t('portal.identify.sendingNotice', { identifier })}
                    </p>
                  )}
                  {hasPassword && (
                    <Button
                      appearance="subtle"
                      style={{ width: '100%' }}
                      onClick={() => setUsePasswordLogin(true)}
                    >
                      {t('portal.identify.signInWithPassword')}
                    </Button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Enter verification code */}
          {step === 'verify' && (
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeBase500 }}>
                {t('portal.verify.title', { channel: verificationType === 'phone' ? t('portal.verify.channelPhone') : t('portal.verify.channelEmail') })}
              </h2>
              {/*
                Past tense, but earned: this screen is only reachable once /portal/verify returned
                ok, i.e. the sender accepted the code for delivery. What it must NOT do is promise
                arrival — "usually" is doing real work there. On 2026-08-26 the carrier took seven
                minutes and the old copy ("We sent a 6-digit code to …") never wavered.
              */}
              <p data-testid="code-sent-notice" style={{ color: '#605e5c', marginBottom: '24px' }}>
                {t('portal.verify.sentNotice', { identifier })}
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.common.verificationCodeLabel')}
                </label>
                <Input
                  value={verificationCode}
                  onChange={(_, data) => setVerificationCode(data.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder={t('portal.verify.codePlaceholder')}
                  style={{ width: '100%', textAlign: 'center', fontSize: tokens.fontSizeBase600, letterSpacing: '8px' }}
                  maxLength={6}
                  onKeyDown={(e) => e.key === 'Enter' && verificationCode.length === 6 && handleVerifyCode()}
                />
              </div>

              <Button
                appearance="primary"
                style={{ width: '100%', marginBottom: '12px' }}
                onClick={handleVerifyCode}
                disabled={verificationCode.length !== 6 || loading}
              >
                {loading ? <Spinner size="tiny" /> : t('portal.verify.verifyButton')}
              </Button>

              <Button
                data-testid="resend-code"
                appearance="subtle"
                style={{ width: '100%', marginBottom: '12px' }}
                onClick={handleResendCode}
                disabled={resendIn > 0 || loading}
              >
                {resendIn > 0 ? t('portal.verify.resendCountdown', { seconds: resendIn }) : t('portal.verify.resendCode')}
              </Button>

              {/*
                Maria's own workaround, promoted to the app's suggestion. Phone channel only — on
                the email channel it would be nonsense — and only while the code box is still
                empty, so it never interrupts someone mid-type.
              */}
              {offerEmailInstead && (
                <Button
                  data-testid="email-fallback-offer"
                  appearance="subtle"
                  style={{ width: '100%', marginBottom: '12px' }}
                  onClick={handleUseEmailInstead}
                >
                  {t('portal.verify.emailFallbackOffer')}
                </Button>
              )}

              <Button
                appearance="subtle"
                style={{ width: '100%' }}
                onClick={() => { setStep('identify'); setVerificationCode(''); setError(null); setShowEmailHint(false); }}
              >
                {t('portal.verify.useDifferent')}
              </Button>
            </div>
          )}

          {/* MFA Step: Enter password to complete verification */}
          {step === 'mfa' && (
            <div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeBase500 }}>{t('portal.mfa.title')}</h2>
              <p style={{ color: '#605e5c', marginBottom: '24px' }}>
                {t('portal.mfa.subtitle')}
              </p>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.common.verificationCodeLabel')}
                </label>
                <Input
                  value={verificationCode}
                  disabled
                  style={{ width: '100%', backgroundColor: '#f5f5f5' }}
                />
                <span style={{ fontSize: tokens.fontSizeBase200, color: '#107c10' }}>{t('portal.mfa.codeVerified')}</span>
              </div>

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.common.passwordLabel')}
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(_, data) => setPassword(data.value)}
                  placeholder={t('portal.common.passwordPlaceholder')}
                  style={{ width: '100%' }}
                  contentBefore={<LockClosedRegular />}
                  onKeyDown={(e) => e.key === 'Enter' && password && handleCompleteMfa()}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <Checkbox
                  checked={trustDevice}
                  onChange={(_, data) => setTrustDevice(!!data.checked)}
                  label={t('portal.mfa.trustDevice')}
                />
                <p style={{ fontSize: tokens.fontSizeBase200, color: '#605e5c', margin: '4px 0 0 28px' }}>
                  {t('portal.mfa.trustDeviceHelper')}
                </p>
              </div>

              <Button
                appearance="primary"
                style={{ width: '100%', marginBottom: '12px' }}
                onClick={handleCompleteMfa}
                disabled={!password || loading}
              >
                {loading ? <Spinner size="tiny" /> : t('portal.mfa.completeButton')}
              </Button>

              <Button
                appearance="subtle"
                style={{ width: '100%' }}
                onClick={() => { setStep('verify'); setPassword(''); setError(null); }}
              >
                {t('portal.mfa.goBack')}
              </Button>
            </div>
          )}

          {/* Step 3: Select tax return */}
          {step === 'returns' && (
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                <h2 style={{ margin: 0, fontSize: tokens.fontSizeBase500 }}>
                  {t('portal.returns.welcome', { clientName: clientName || t('portal.returns.defaultClientName') })}
                </h2>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => {
                    sessionStorage.removeItem(SESSION_STORAGE_KEY);
                    setSessionToken(null);
                    setReturns([]);
                    setIdentifier('');
                    setStep('identify');
                  }}
                >
                  {t('portal.returns.signOut')}
                </Button>
              </div>
              <p style={{ color: '#605e5c', marginBottom: '24px' }}>
                {returns.length > 0
                  ? t('portal.returns.subtitleWithReturns')
                  : t('portal.returns.subtitleNoReturns')}
              </p>

              {/* The link named a return this client cannot see. Say so — see deepLinkTargetMissing. */}
              {deepLinkTargetMissing && (
                <div
                  role="status"
                  data-testid="deep-link-target-missing"
                  style={{
                    padding: '12px 16px',
                    backgroundColor: '#fff4ce',
                    border: '1px solid #f7d574',
                    borderLeft: '4px solid #d18f00',
                    borderRadius: '4px',
                    marginBottom: '16px',
                    color: '#3b3b3b',
                    fontSize: tokens.fontSizeBase300,
                    lineHeight: 1.5,
                  }}
                >
                  {t('portal.returns.deepLinkMissing')}
                </div>
              )}

              {/* Tax Returns List */}
              {returns.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '16px' }}>
                  {returns.map(r => (
                    <div
                      key={r.id}
                      onClick={() => handleSelectReturn(r)}
                      style={{
                        padding: '16px',
                        border: '1px solid #e1e1e1',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.borderColor = '#6b7a3d';
                        e.currentTarget.style.backgroundColor = '#f2f5ec';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.borderColor = '#e1e1e1';
                        e.currentTarget.style.backgroundColor = 'white';
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        {r.returnType === 'business' ? <BuildingRegular /> : <PersonRegular />}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 600 }}>
                            {t('portal.returns.card.title', {
                              year: r.taxYear,
                              type: r.returnType === 'business' ? t('portal.returns.card.typeBusiness') : t('portal.returns.card.typePersonal'),
                            })}
                          </div>
                          <div style={{ fontSize: tokens.fontSizeBase300, color: '#605e5c' }}>
                            {r.clientName}
                            {r.filingType === 'joint' && (
                              <span>
                                {r.spouseName
                                  ? t('portal.returns.card.filingJointlyWithSpouse', { spouseName: r.spouseName })
                                  : t('portal.returns.card.filingJointlyPlain')}
                              </span>
                            )}
                          </div>
                          {r.scheduledDate && (
                            <div style={{ fontSize: tokens.fontSizeBase300, color: '#323130', marginTop: '4px', fontWeight: 500 }}>
                              {t('portal.returns.card.scheduled', {
                                date: new Date(r.scheduledDate + 'T00:00:00').toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }),
                              })}
                            </div>
                          )}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
                          <Badge appearance="filled" color="informative">
                            {r.statusDisplayName || t('portal.returns.card.statusDefault')}
                          </Badge>
                          <Badge
                            appearance={r.allDocumentsReceived ? 'filled' : 'outline'}
                            color={r.allDocumentsReceived ? 'success' : 'warning'}
                          >
                            {r.allDocumentsReceived ? t('portal.returns.card.docsReceived') : t('portal.returns.card.docsNeeded')}
                          </Badge>
                        </div>
                      </div>
                      {r.missingDocuments?.length > 0 && (
                        <div style={{ marginTop: '8px', fontSize: '13px', color: '#605e5c' }}>
                          {t('portal.returns.card.missing', { list: r.missingDocuments.slice(0, 3).join(', ') })}
                          {r.missingDocuments.length > 3 && t('portal.returns.card.missingMore', { count: r.missingDocuments.length - 3 })}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* General Upload Button - Always Visible */}
              <div
                onClick={handleGeneralUpload}
                style={{
                  padding: '16px',
                  border: '2px dashed #6b7a3d',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                  backgroundColor: '#f2f5ec',
                  textAlign: 'center',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.backgroundColor = '#e8eddf';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundColor = '#f5f9ff';
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px' }}>
                  <ArrowUploadRegular style={{ fontSize: tokens.fontSizeBase600, color: '#1a1a1a' }} />
                  <div>
                    <div style={{ fontWeight: 600, color: '#1a1a1a' }}>{t('portal.common.uploadDocuments')}</div>
                    <div style={{ fontSize: '13px', color: '#605e5c' }}>
                      {returns.length > 0 ? t('portal.returns.generalUpload.subtitleWithReturns') : t('portal.returns.generalUpload.subtitleNoReturns')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Set Password Option */}
              {!hasPassword && (
                <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #e1e1e1' }}>
                  <Button
                    appearance="subtle"
                    style={{ width: '100%' }}
                    onClick={() => {
                      setPassword('');
                      setConfirmPassword('');
                      setStep('setPassword');
                    }}
                    icon={<KeyRegular />}
                  >
                    {t('portal.returns.setPasswordButton')}
                  </Button>
                </div>
              )}
            </div>
          )}

          {/* Step: Set Password */}
          {step === 'setPassword' && (
            <div>
              <Button
                appearance="subtle"
                onClick={() => { setStep('returns'); setPassword(''); setConfirmPassword(''); }}
                style={{ marginBottom: '16px', padding: '4px 8px' }}
              >
                {t('portal.common.backButton')}
              </Button>

              <h2 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeBase500 }}>{t('portal.setPassword.title')}</h2>
              <p style={{ color: '#605e5c', marginBottom: '24px' }}>
                {t('portal.setPassword.subtitle')}
              </p>

              {successMessage && (
                <div style={{
                  padding: '12px 16px',
                  backgroundColor: '#dff6dd',
                  color: '#107c10',
                  borderRadius: '4px',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                  <CheckmarkCircleRegular />
                  {successMessage}
                </div>
              )}

              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.common.passwordLabel')}
                </label>
                <Input
                  type="password"
                  value={password}
                  onChange={(_, data) => setPassword(data.value)}
                  placeholder={t('portal.setPassword.passwordPlaceholder')}
                  style={{ width: '100%' }}
                  contentBefore={<LockClosedRegular />}
                />
              </div>

              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.setPassword.confirmPasswordLabel')}
                </label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(_, data) => setConfirmPassword(data.value)}
                  placeholder={t('portal.setPassword.confirmPasswordPlaceholder')}
                  style={{ width: '100%' }}
                  contentBefore={<LockClosedRegular />}
                  onKeyDown={(e) => e.key === 'Enter' && password && confirmPassword && handleSetPassword()}
                />
              </div>

              <Button
                appearance="primary"
                style={{ width: '100%', backgroundColor: '#1a1a1a', color: '#ffffff', borderColor: '#1a1a1a' }}
                onClick={handleSetPassword}
                disabled={!password || !confirmPassword || password.length < 8 || loading}
              >
                {loading ? <Spinner size="tiny" /> : t('portal.setPassword.submitButton')}
              </Button>
            </div>
          )}

          {/* Step 4: Upload documents */}
          {step === 'upload' && (selectedReturn || isGeneralUpload) && (
            <div>
              <Button
                appearance="subtle"
                onClick={() => { setStep('returns'); setUploadedFiles([]); setIsGeneralUpload(false); setSelectedDocType('other'); }}
                style={{ marginBottom: '16px', padding: '4px 8px' }}
              >
                {t('portal.common.backButton')}
              </Button>

              <h2 style={{ margin: '0 0 8px 0', fontSize: tokens.fontSizeBase500 }}>
                {t('portal.common.uploadDocuments')}
              </h2>
              <p style={{ color: '#605e5c', marginBottom: '24px' }}>
                {isGeneralUpload
                  ? t('portal.upload.subtitleGeneral')
                  : t('portal.upload.subtitleSpecific', {
                      year: selectedReturn!.taxYear,
                      type: selectedReturn!.returnType === 'business' ? t('portal.returns.card.typeBusiness') : t('portal.returns.card.typePersonal'),
                      clientName: selectedReturn!.clientName,
                      jointSuffix: selectedReturn!.filingType === 'joint' && selectedReturn!.spouseName
                        ? t('portal.upload.jointSuffix', { spouseName: selectedReturn!.spouseName })
                        : '',
                      statusSuffix: selectedReturn!.statusDisplayName
                        ? t('portal.upload.statusSuffix', { status: selectedReturn!.statusDisplayName })
                        : '',
                    })}
              </p>

              {/* Document Type Selector */}
              <div style={{ marginBottom: '16px' }}>
                <label style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>
                  {t('portal.upload.docTypeLabel')}
                </label>
                <Dropdown
                  value={
                    selectedDocType === 'other' ? t('portal.upload.docType.other') :
                    selectedDocType === 'signed_8879' ? t('portal.upload.docType.signed8879') :
                    selectedDocType === 'engagement_letter' ? t('portal.upload.docType.engagementLetter') :
                    selectedDocType === 'completed_return' ? t('portal.upload.docType.completedReturn') : t('portal.upload.docType.fallback')
                  }
                  onOptionSelect={(_, data) => setSelectedDocType(data.optionValue || 'other')}
                  style={{ width: '100%' }}
                >
                  <Option value="other">{t('portal.upload.docType.other')}</Option>
                  <Option value="signed_8879">{t('portal.upload.docType.signed8879')}</Option>
                  <Option value="engagement_letter">{t('portal.upload.docType.engagementLetter')}</Option>
                  <Option value="completed_return">{t('portal.upload.docType.completedReturn')}</Option>
                </Dropdown>
                <p style={{ fontSize: tokens.fontSizeBase200, color: '#605e5c', margin: '4px 0 0' }}>
                  {t('portal.upload.docTypeHelper')}
                </p>
              </div>

              {/* Drop zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                style={{
                  border: '2px dashed #c8c8c8',
                  borderRadius: '8px',
                  padding: '32px',
                  textAlign: 'center',
                  marginBottom: '16px',
                  backgroundColor: '#fafafa',
                  cursor: 'pointer',
                }}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <ArrowUploadRegular style={{ fontSize: '48px', color: '#1a1a1a' }} />
                <p style={{ margin: '16px 0 8px', fontWeight: 500 }}>
                  {t('portal.upload.dropzone.title')}
                </p>
                <p style={{ margin: 0, color: '#605e5c', fontSize: tokens.fontSizeBase300 }}>
                  {t('portal.upload.dropzone.subtitle')}
                </p>
                <input
                  id="file-input"
                  type="file"
                  multiple
                  style={{ display: 'none' }}
                  onChange={(e) => handleFileUpload(e.target.files)}
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx"
                />
              </div>

              {/* Uploaded files list */}
              {uploadedFiles.length > 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {uploadedFiles.map((file, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                        padding: '12px',
                        backgroundColor: file.status === 'error' ? '#fde7e9' : '#f5f5f5',
                        borderRadius: '4px',
                      }}
                    >
                      <DocumentRegular />
                      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {file.name}
                      </span>
                      {file.status === 'pending' && <span style={{ color: '#605e5c' }}>{t('portal.upload.fileStatus.waiting')}</span>}
                      {file.status === 'uploading' && <Spinner size="tiny" />}
                      {file.status === 'success' && (
                        <CheckmarkCircleRegular style={{ color: '#107c10' }} />
                      )}
                      {file.status === 'error' && (
                        <span style={{ color: '#a4262c', fontSize: '13px' }}>{file.error}</span>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Receipt. The old banner named no file, so the client could not quote it back at
                  anyone -- which is precisely what left Maria Stopa with nothing when staff said
                  they could not find her uploads. */}
              {receipt && receipt.files.length > 0 && (
                <div
                  data-testid="upload-receipt"
                  style={{
                    marginTop: '16px',
                    padding: '16px',
                    backgroundColor: '#dff6dd',
                    borderRadius: '8px',
                  }}
                >
                  <div style={{ textAlign: 'center' }}>
                    <CheckmarkCircleRegular style={{ fontSize: tokens.fontSizeHero800, color: '#107c10' }} />
                    <p style={{ margin: '8px 0 0', fontWeight: 600, color: '#107c10' }}>
                      {t('portal.upload.receipt.summary', { count: receipt.files.length })}
                    </p>
                  </div>

                  <ul style={{ margin: '12px 0 0', paddingLeft: '20px', color: '#1a1a1a', fontSize: tokens.fontSizeBase300 }}>
                    {receipt.files.map((f, i) => (
                      <li key={i} style={{ marginBottom: '4px' }}>
                        {f.fileName}
                        {f.attached ? null : (
                          <span style={{ color: '#605e5c' }}>{t('portal.upload.receipt.pendingFiling')}</span>
                        )}
                      </li>
                    ))}
                  </ul>

                  {receipt.attachedCount > 0 && (
                    <p style={{ margin: '12px 0 0', color: '#1a1a1a', fontSize: tokens.fontSizeBase300 }}>
                      {t('portal.upload.receipt.attached', {
                        years: receipt.taxYears.length > 0
                          ? receipt.taxYears.join(t('portal.upload.receipt.yearsJoiner'))
                          : t('portal.upload.receipt.attachedCurrentFallback'),
                      })}
                    </p>
                  )}

                  {receipt.generalCount > 0 && (
                    <p style={{ margin: '8px 0 0', color: '#1a1a1a', fontSize: tokens.fontSizeBase300 }}>
                      {t('portal.upload.receipt.generalMatch', { count: receipt.generalCount })}
                    </p>
                  )}

                  {receipt.emailed && (
                    <p style={{ margin: '12px 0 0', color: '#605e5c', fontSize: '13px' }}>
                      {t('portal.upload.receipt.emailed')}
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </Card>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          color: 'white',
          marginTop: '24px',
          opacity: 0.9,
          fontSize: tokens.fontSizeBase300,
        }}>
          <p style={{ margin: 0, fontWeight: 600 }}>{t('portal.brand.name')}</p>
          <p style={{ margin: '4px 0 0' }}>{t('portal.footer.services')}</p>
          <p style={{ margin: '4px 0 0' }}>{t('portal.footer.contact')}</p>
        </div>
      </div>
    </div>
  );
}
